import { useMemo } from 'react';
import { DisplayContext, type DisplayEnv } from './context.tsx';
import { fmtAsOf } from './format.ts';
import { pairBlocks, parseScreen, type ViewBlock, type ViewScreen } from './screen-model.ts';
import { TableBlock } from './TableBlock.tsx';
import { ChartBlock } from './ChartBlock.tsx';
import { KpiBlock } from './KpiBlock.tsx';
import { TextBlock } from './TextBlock.tsx';
import { HtmlBlock } from './HtmlBlock.tsx';
import { AppBlock } from './AppBlock.tsx';
import { FileBlock } from './FileBlock.tsx';
import { RawBlock } from './RawBlock.tsx';
import { PairView } from './PairView.tsx';
import { S } from './strings.ts';
import { Expand } from './Expand.tsx';

/** בלוק לפי סוגו; expandable — עטוף ב-Expand עם "הגדל" בראשו (כל סוג חוץ מ-text/raw; לא ב-PDF). */
export function BlockView({ block, expandable = false }: { block: ViewBlock; expandable?: boolean }) {
  const view = <BlockByType block={block} />;
  if (!expandable || !EXPANDABLE.has(block.type)) return view;
  return <Expand labelHe={'titleHe' in block && typeof block.titleHe === 'string' ? block.titleHe : undefined}>{view}</Expand>;
}

const EXPANDABLE = new Set(['table', 'chart', 'kpi', 'html', 'app', 'file']);

function BlockByType({ block }: { block: ViewBlock }) {
  switch (block.type) {
    case 'table':
      return <TableBlock block={block} />;
    case 'chart':
      return <ChartBlock block={block} />;
    case 'kpi':
      return <KpiBlock block={block} />;
    case 'text':
      return <TextBlock block={block} />;
    case 'html':
      return <HtmlBlock block={block} />;
    case 'app':
      return <AppBlock block={block} />;
    case 'file':
      return <FileBlock block={block} />;
    default:
      return <RawBlock block={block} />;
  }
}

export type ScreenProps = {
  /** Screen מה-api — לא מאומת; כל בלוק מאומת לבד, לא-מוכר → raw. */
  screen: unknown;
  /** דורס את asOf של ה-Screen (למשל asOf של הריצה). */
  asOf?: string | null;
  /** "רץ בשם <יוצר>" (run_as=creator, 09). */
  runAsHe?: string | null;
} & Omit<DisplayEnv, 'asOfText'>;

/** מצייר blocks לפי סדר; notices למעלה; "נכון ל-" תמיד (11 §1). */
export function Screen({ screen, asOf, runAsHe, ...env }: ScreenProps) {
  const view: ViewScreen = useMemo(() => parseScreen(screen), [screen]);
  const effAsOf = asOf === undefined ? view.asOf : asOf;
  const asOfText = fmtAsOf(effAsOf);
  const groups = useMemo(() => pairBlocks(view.blocks), [view.blocks]);
  return (
    <DisplayContext.Provider value={{ ...env, asOfText }}>
      <div className="ab-screen" data-testid="screen">
        <div className="ab-screen-meta">
          <span className="ab-asof" data-testid="as-of" data-null={effAsOf ? undefined : 'true'}>
            {asOfText}
          </span>
          {runAsHe && (
            <span className="ab-runas" data-testid="run-as">
              {S.runAs(runAsHe)}
            </span>
          )}
        </div>
        {view.notices.map((n, i) => (
          <div key={i} className={`ab-notice ab-notice-${n.kind}`} role={n.kind === 'error' ? 'alert' : 'status'}>
            <strong>{S.notice[n.kind]}:</strong> {n.textHe}
          </div>
        ))}
        {groups.map((g) =>
          g.kind === 'pair' ? (
            env.print ? (
              <PairView key={`${g.chart.id}+${g.table.id}`} chart={g.chart} table={g.table} />
            ) : (
              <Expand key={`${g.chart.id}+${g.table.id}`} labelHe={g.table.titleHe ?? g.chart.titleHe}>
                <PairView chart={g.chart} table={g.table} />
              </Expand>
            )
          ) : (
            <BlockView key={g.block.id} block={g.block} expandable={!env.print} />
          ),
        )}
      </div>
    </DisplayContext.Provider>
  );
}
